import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { useTrack, TrackEvents } from '@/composables/useTrack';
import { useUserStore } from '@/store/useUserStore';
import { ItemType, useViewerItemStore } from '../store/useViewerItemStore';
import { useAuthModals } from '@/composables/useAuthModals';
import { useReactionsStore } from '../store/useReactionsStore';

export type AddReactionEvent = { name: string };

export const useReactions = () => {
  const itemStore = useViewerItemStore();
  const userStore = useUserStore();
  const reactionsStore = useReactionsStore();

  const { hideBranding, reactionsDisabled, currentPlaybackTime, type } = storeToRefs(itemStore);
  const isVideo = computed(() => type.value === ItemType.VIDEO);
  const { isLoggedIn } = storeToRefs(userStore);
  const { track } = useTrack();
  const { showSignUpModal } = useAuthModals();

  const isProcessing = ref(false);

  const countColor = (name: string, index: number, isHovering: number | null) => {
    const isActive = reactionsStore.isActiveReaction(name);

    if (isActive && isHovering == index) return 'text-white';
    if (isActive && isHovering !== index) return 'text-white';
    if (!isActive && isHovering == index) return 'text-black';
    if (!isActive && isHovering !== index) return 'text-black';
  };

  const buttonColor = (name: string, index: number, isHovering: number | null) => {
    const isActive = reactionsStore.isActiveReaction(name);

    if (isActive && isHovering == index) return 'bg-blue-600 ';
    if (isActive && isHovering !== index) return 'bg-blue-500 ';
    if (!isActive && isHovering == index) return 'bg-zinc-50 dark:bg-zinc-800';
    if (!isActive && isHovering !== index) return 'bg-white dark:bg-transparent';
  };

  const onClick = async (id: string | undefined, name: string) => {
    if (isProcessing.value || !id) {
      return;
    }

    isProcessing.value = true;

    try {
      // A video reaction always adds a marker at the current playback time, so it
      // never toggles off; non-video reactions keep like/unlike behavior.
      if (isVideo.value) {
        await reactionsStore.addReaction(name, id, currentPlaybackTime.value);
      } else if (reactionsStore.isActiveReaction(name)) {
        await reactionsStore.removeReaction(name);
      } else {
        await reactionsStore.addReaction(name, id);
      }

      if (!isLoggedIn?.value && !hideBranding.value) {
        track(TrackEvents.PROMPT_SIGN_UP_REACTION, { reaction: name });
        await showSignUpModal({ sidebarText: 'Join Zight to add your name to the conversation' });
      }
    } finally {
      isProcessing.value = false;
    }
  };

  const load = (id: string) => reactionsStore.load(id);
  return { reactionsDisabled, countColor, buttonColor, onClick, load, reactionsCount: reactionsStore.reactionsCount };
};
